import { useEffect, useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  Divider,
  FormControlLabel,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import { useTranslation } from 'react-i18next';

import { useSnackBar } from '@frontend/features/common/components/SnackBarContext';
import {
  useGetWorkflowSteps,
  useGetWorkflowsByAgency,
} from '@frontend/features/workflows/queries';
import { useGetFieldBlockByWorkflowId } from '@frontend/features/custom-fields/queries';

import {
  CopilotConfigDto,
  CopilotConfigFormValues,
  TRAVEL_FACET_OPTIONS,
  useSaveCopilotConfig,
} from '../queries';

interface Props {
  agencyId: number;
  config?: CopilotConfigDto;
  onClose: () => void;
}

const checkboxIcon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkboxCheckedIcon = <CheckBoxIcon fontSize="small" />;

const CAPABILITY_TOGGLES: {
  name: keyof CopilotConfigFormValues;
  labelKey: string;
}[] = [
  { name: 'detectClientName', labelKey: 'copilot.configs.form.detectClientName' },
  {
    name: 'detectCustomFields',
    labelKey: 'copilot.configs.form.detectCustomFields',
  },
  {
    name: 'analyzeInterestScore',
    labelKey: 'copilot.configs.form.analyzeInterestScore',
  },
  { name: 'suggestReplies', labelKey: 'copilot.configs.form.suggestReplies' },
  { name: 'salesCoaching', labelKey: 'copilot.configs.form.salesCoaching' },
  { name: 'useExternalApis', labelKey: 'copilot.configs.form.useExternalApis' },
];

const DEFAULT_VALUES: CopilotConfigFormValues = {
  workflowId: null,
  stepIds: [],
  enabled: true,
  detectClientName: false,
  detectCustomFields: false,
  analyzeInterestScore: false,
  suggestReplies: false,
  salesCoaching: false,
  useExternalApis: false,
  externalApiFacets: [],
  targetCustomFields: [],
  passiveModel: 'gpt-4o-mini',
  activeModel: 'gpt-4o',
  stepContext: '',
};

export const CopilotConfigForm = ({ agencyId, config, onClose }: Props) => {
  const { addToast } = useSnackBar();
  const { t } = useTranslation();
  const mutation = useSaveCopilotConfig(agencyId, config?.id);

  const { control, handleSubmit, reset, watch, setValue } =
    useForm<CopilotConfigFormValues>({ defaultValues: DEFAULT_VALUES });

  const { data: workflowsData } = useGetWorkflowsByAgency({
    agencyId: String(agencyId),
    pageSize: 1000,
    currentPage: 1,
    search: '',
  });

  const workflowId = watch('workflowId');
  const useExternalApisOn = watch('useExternalApis');
  const detectCustomFieldsOn = watch('detectCustomFields');
  const { data: steps } = useGetWorkflowSteps(workflowId || undefined);
  const { data: fieldBlocks } = useGetFieldBlockByWorkflowId({
    id: workflowId || '',
  });

  const workflowOptions = useMemo(
    () => (workflowsData?.items || []).map((wf) => ({ id: wf.id, name: wf.name })),
    [workflowsData],
  );
  const stepOptions = useMemo(
    () => (steps || []).map((s) => ({ id: s.id, name: s.name })),
    [steps],
  );
  // Custom fields del workflow elegido (dinámicos): opciones del multi-select de
  // campos objetivo. Se aplanan todos los field blocks del workflow.
  const customFieldOptions = useMemo(
    () =>
      (fieldBlocks || []).flatMap((block) =>
        (block.fields || []).map((f) => ({
          value: f.name,
          label: f.label || f.name,
        })),
      ),
    [fieldBlocks],
  );

  useEffect(() => {
    if (config) {
      reset({
        workflowId: config.workflowId,
        stepIds: config.stepIds || [],
        enabled: config.enabled,
        detectClientName: config.detectClientName,
        detectCustomFields: config.detectCustomFields,
        analyzeInterestScore: config.analyzeInterestScore,
        suggestReplies: config.suggestReplies,
        salesCoaching: config.salesCoaching,
        useExternalApis: config.useExternalApis,
        externalApiFacets: config.externalApiFacets || [],
        targetCustomFields: config.targetCustomFields || [],
        passiveModel: config.passiveModel,
        activeModel: config.activeModel,
        stepContext: config.stepContext || '',
      });
    }
  }, [config, reset]);

  const onSubmit = async (values: CopilotConfigFormValues) => {
    try {
      await mutation.mutateAsync(values);
      addToast({ message: t('copilot.configs.saveSuccess'), color: 'success' });
      onClose();
    } catch {
      addToast({ message: t('copilot.configs.saveError'), color: 'error' });
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Box display="flex" flexDirection="column" gap={2} marginTop={1}>
        <Controller
          name="enabled"
          control={control}
          render={({ field }) => (
            <FormControlLabel
              control={
                <Switch
                  checked={field.value}
                  onChange={(e) => field.onChange(e.target.checked)}
                />
              }
              label={t('copilot.configs.form.enabled')}
            />
          )}
        />

        <Controller
          name="workflowId"
          control={control}
          render={({ field }) => {
            const selected =
              workflowOptions.find((o) => o.id === field.value) || null;
            return (
              <Autocomplete
                options={workflowOptions}
                value={selected}
                onChange={(_, newValue) => {
                  field.onChange(newValue?.id ?? null);
                  // Los steps y los campos objetivo pertenecen al workflow: al
                  // cambiarlo, limpiamos ambos (los CF viejos ya no aplican).
                  setValue('stepIds', []);
                  setValue('targetCustomFields', []);
                }}
                getOptionLabel={(option) => option.name}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t('copilot.configs.form.workflow')}
                    placeholder={t('copilot.configs.form.allWorkflows')}
                  />
                )}
              />
            );
          }}
        />

        <Controller
          name="stepIds"
          control={control}
          render={({ field }) => {
            const selectedIds: string[] = field.value || [];
            const selectedOptions = stepOptions.filter((opt) =>
              selectedIds.includes(opt.id),
            );
            return (
              <Autocomplete
                multiple
                options={stepOptions}
                value={selectedOptions}
                disabled={!workflowId}
                onChange={(_, newValue) =>
                  field.onChange(newValue.map((o) => o.id))
                }
                getOptionLabel={(option) => option.name}
                disableCloseOnSelect
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      {...getTagProps({ index })}
                      key={option.id}
                      label={option.name}
                      size="small"
                    />
                  ))
                }
                renderOption={(props, option, { selected }) => (
                  <li {...props} key={option.id}>
                    <Checkbox
                      icon={checkboxIcon}
                      checkedIcon={checkboxCheckedIcon}
                      style={{ marginRight: 8 }}
                      checked={selected}
                    />
                    {option.name}
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t('copilot.configs.form.steps')}
                    placeholder={t('copilot.configs.form.allSteps')}
                  />
                )}
              />
            );
          }}
        />

        <Divider textAlign="left">
          <Typography variant="caption" color="text.secondary">
            {t('copilot.configs.form.capabilities')}
          </Typography>
        </Divider>

        <Box display="grid" gridTemplateColumns="1fr 1fr" columnGap={1}>
          {CAPABILITY_TOGGLES.map((toggle) => (
            <Controller
              key={toggle.name}
              name={toggle.name}
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Switch
                      checked={Boolean(field.value)}
                      onChange={(e) => field.onChange(e.target.checked)}
                    />
                  }
                  label={t(toggle.labelKey)}
                />
              )}
            />
          ))}
        </Box>

        {detectCustomFieldsOn && workflowId && (
          <Controller
            name="targetCustomFields"
            control={control}
            render={({ field }) => (
              <Autocomplete
                multiple
                options={customFieldOptions}
                value={customFieldOptions.filter((o) =>
                  (field.value || []).includes(o.value),
                )}
                onChange={(_, newValue) =>
                  field.onChange(newValue.map((o) => o.value))
                }
                getOptionLabel={(option) => option.label}
                isOptionEqualToValue={(opt, val) => opt.value === val.value}
                disableCloseOnSelect
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      {...getTagProps({ index })}
                      key={option.value}
                      label={option.label}
                      size="small"
                    />
                  ))
                }
                renderOption={(props, option, { selected }) => (
                  <li {...props} key={option.value}>
                    <Checkbox
                      icon={checkboxIcon}
                      checkedIcon={checkboxCheckedIcon}
                      style={{ marginRight: 8 }}
                      checked={selected}
                    />
                    {option.label}
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t('copilot.configs.form.targetCustomFields')}
                    placeholder={t(
                      'copilot.configs.form.targetCustomFieldsPlaceholder',
                    )}
                    helperText={t(
                      'copilot.configs.form.targetCustomFieldsHelper',
                    )}
                  />
                )}
              />
            )}
          />
        )}

        {useExternalApisOn && (
          <Controller
            name="externalApiFacets"
            control={control}
            render={({ field }) => (
              <Autocomplete
                multiple
                options={TRAVEL_FACET_OPTIONS}
                value={TRAVEL_FACET_OPTIONS.filter((o) =>
                  (field.value || []).includes(o.value),
                )}
                onChange={(_, newValue) =>
                  field.onChange(newValue.map((o) => o.value))
                }
                getOptionLabel={(option) => option.label}
                disableCloseOnSelect
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      {...getTagProps({ index })}
                      key={option.value}
                      label={option.label}
                      size="small"
                    />
                  ))
                }
                renderOption={(props, option, { selected }) => (
                  <li {...props} key={option.value}>
                    <Checkbox
                      icon={checkboxIcon}
                      checkedIcon={checkboxCheckedIcon}
                      style={{ marginRight: 8 }}
                      checked={selected}
                    />
                    {option.label}
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t('copilot.configs.form.externalApiFacets')}
                    placeholder={t(
                      'copilot.configs.form.externalApiFacetsPlaceholder',
                    )}
                    helperText={t(
                      'copilot.configs.form.externalApiFacetsHelper',
                    )}
                  />
                )}
              />
            )}
          />
        )}

        <Controller
          name="stepContext"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              label={t('copilot.configs.form.stepContext')}
              placeholder={t('copilot.configs.form.stepContextPlaceholder')}
              helperText={t('copilot.configs.form.stepContextHelper')}
              multiline
              minRows={3}
              fullWidth
            />
          )}
        />

        <Stack direction="row" spacing={2}>
          <Controller
            name="passiveModel"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label={
                  <Box
                    component="span"
                    sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
                  >
                    {t('copilot.configs.form.passiveModel')}
                    <Tooltip title={t('copilot.configs.form.passiveModelHelper')}>
                      <InfoOutlined sx={{ fontSize: 14 }} />
                    </Tooltip>
                  </Box>
                }
                helperText={t('copilot.configs.form.passiveModelHelper')}
                fullWidth
              />
            )}
          />
          <Controller
            name="activeModel"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label={
                  <Box
                    component="span"
                    sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
                  >
                    {t('copilot.configs.form.activeModel')}
                    <Tooltip title={t('copilot.configs.form.activeModelHelper')}>
                      <InfoOutlined sx={{ fontSize: 14 }} />
                    </Tooltip>
                  </Box>
                }
                helperText={t('copilot.configs.form.activeModelHelper')}
                fullWidth
              />
            )}
          />
        </Stack>

        <Button variant="contained" type="submit" disabled={mutation.isLoading}>
          {config
            ? t('copilot.configs.form.update')
            : t('copilot.configs.form.create')}
        </Button>
      </Box>
    </form>
  );
};
